import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'bootstrap';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { MenuItem, MenuWithSubLinks, VisibleSubLinks } from '@types';
import styles from '../Menu.module.css';
import { useUrlAnalyser } from '@hooks/useUrlAnalyser';

interface MenuItemWithSubItemProps {
    item: MenuItem;
    show: boolean;
    activeItem: string | null;
    setActiveItem: (value: string | null) => void;
    showSubcategoriesOf: VisibleSubLinks;
    setShowSubcategoriesOf: (value: VisibleSubLinks) => void;
    closeSidebar: () => void;
}

/**
 * Holds a list of link to pages.
 * Does not directly link to a page (example: Products, Services, Merchants).
 * Used on Navigation component.
 */
export const MenuItemWithSubItem: FC<MenuItemWithSubItemProps> = ({
    item,
    activeItem,
    setActiveItem,
    showSubcategoriesOf,
    setShowSubcategoriesOf,
    show,
    closeSidebar,
}) => {
    const pageSize = useMediaQuery();
    const isMobile = ['xs', 'sm'].includes(pageSize);

    const [collapseElement, setCollapseElement] = useState<Collapse | null>(null);

    const collapseRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const collapseInstance = Collapse.getOrCreateInstance(node, { toggle: false });
            setCollapseElement(collapseInstance);

            if (activeItem === item.id) {
                collapseInstance.show();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            id={item.id}
            className={`accordion-item ${styles['accordion-item']} nav-item ${styles['nav-item']} ${show ? 'show' : ''}`}
            style={{ display: show ? 'block' : 'none' }}>
            {isMobile ? (
                <>
                    {/* menu item name - collapses content */}
                    <button
                        type="button"
                        className={`accordion-button ${styles['accordion-button']} d-md-none`}
                        onClick={() => {
                            if (item.id !== activeItem) {
                                collapseElement?.show();
                                setActiveItem(item.id);
                            } else {
                                collapseElement?.hide();
                                setActiveItem(null);
                            }
                        }}>
                        <span className={`nav-link ${styles['nav-link']}`}>
                            <Container classes={styles.container}>
                                <Typography tagVariant={TypographyTagVariants.span}>{item.label}</Typography>
                            </Container>
                        </span>
                    </button>

                    <div ref={collapseRef} id={`menu-accordion-${item.id}`} className="accordion-collapse collapse">
                        <div className={`accordion-body p-0 ${styles['menu-sublevels']}`}>
                            <div className="horizontal-accordion">
                                <MenuLinkContent
                                    item={item}
                                    setActiveItem={setActiveItem}
                                    showSubcategoriesOf={showSubcategoriesOf}
                                    setShowSubcategoriesOf={setShowSubcategoriesOf}
                                    closeSidebar={closeSidebar}
                                />
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* menu item name */}
                    <div className={styles['menu-label']}>
                        <div className={`nav-link ${styles['nav-link']} d-none d-md-block`}>
                            <Container classes={styles.container}>
                                <Typography tagVariant={TypographyTagVariants.span}>{item.label}</Typography>
                            </Container>
                        </div>
                    </div>

                    <div className={`p-0 ${styles['menu-sublevels']}`}>
                        <MenuLinkContent
                            item={item}
                            setActiveItem={setActiveItem}
                            showSubcategoriesOf={showSubcategoriesOf}
                            setShowSubcategoriesOf={setShowSubcategoriesOf}
                            closeSidebar={closeSidebar}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

interface LinkWithoutSubLinksProps {
    label: string;
    url: string;
    closeSidebar: () => void;
}

const LinkWithoutSubLinks: FC<LinkWithoutSubLinksProps> = ({ label, url, closeSidebar }) => {
    const { isSameRoute } = useUrlAnalyser();

    return (
        <Anchor
            href={url || ''}
            mode={AnchorMode.none}
            classes={`nav-link ${styles['nav-link']}`}
            onClick={() => isSameRoute(url) && closeSidebar()}>
            <Container classes={styles.container}>
                <Typography tagVariant={TypographyTagVariants.span}>{label}</Typography>
            </Container>
        </Anchor>
    );
};

interface SubLinkProps {
    label: string;
    url: string;
    closeSidebar: () => void;
}

const SubLink: FC<SubLinkProps> = ({ label, url, closeSidebar }) => {
    const { isSameRoute } = useUrlAnalyser();

    return (
        <li className={styles['multilevel-menu-subcategory']}>
            <Anchor
                href={url}
                mode={AnchorMode.none}
                classes={`nav-link ${styles['nav-link']}`}
                onClick={() => isSameRoute(url) && closeSidebar()}>
                <Container classes={styles.container}>
                    <Typography tagVariant={TypographyTagVariants.span}>{label}</Typography>
                </Container>
            </Anchor>
        </li>
    );
};

interface SubLinksAggregatorProps {
    menuId: string;
    item: MenuWithSubLinks;
    setActiveItem: (value: string | null) => void;
    showSubcategoriesOf: VisibleSubLinks;
    setShowSubcategoriesOf: (value: VisibleSubLinks) => void;
    closeSidebar: () => void;
}

const SubLinksAggregator: FC<SubLinksAggregatorProps> = ({
    menuId,
    item,
    showSubcategoriesOf,
    setShowSubcategoriesOf,
    setActiveItem,
    closeSidebar,
}) => {
    const { t } = useTranslation();

    const translations = {
        backMenu: t('menu-component.titles-and-buttons.back-to-menu'),
    };

    function goBackToCategories(event: React.MouseEvent<HTMLElement, MouseEvent>) {
        event.stopPropagation();
        setShowSubcategoriesOf(null);
    }

    // on mobile, when going back to categories,
    // all other menus should be shown again
    function goBackToCategoriesOnMobile(event: React.MouseEvent<HTMLElement, MouseEvent>) {
        event.stopPropagation();
        goBackToCategories(event);
    }

    function showSubcategories(menuId: string, linkId: string) {
        setShowSubcategoriesOf({ menuItemId: menuId, subLinksAggregatorId: linkId });
        // when displaying subcategories, only one menu will be visible
        setActiveItem(menuId);
    }

    return (
        <>
            <div className="d-none d-md-block">
                <button className={styles['go-back-to-menu-categories']} onClick={goBackToCategories}>
                    <i className="fa-light fa-arrow-left me-2" />
                    {translations.backMenu}
                </button>
            </div>
            <button
                type="button"
                className={`nav-link ${styles['nav-link']} d-flex w-100`}
                onClick={() => showSubcategories(menuId, `id-${menuId}-${item.label}`)}>
                <Container classes={`${styles.container} d-flex justify-content-between align-items-center`}>
                    <Typography
                        tagVariant={TypographyTagVariants.span}
                        classes="d-flex justify-content-between align-items-center w-100">
                        <>
                            <Typography
                                tagVariant={TypographyTagVariants.span}
                                fontWeight={showSubcategoriesOf === null ? '400' : '800'}>
                                <>
                                    <div className="d-inline-block d-md-none">
                                        <i
                                            className={`fa-light fa-arrow-left me-2 ${styles['go-back-to-all-menus']}`}
                                            onClick={(event) => goBackToCategoriesOnMobile(event)}
                                        />
                                    </div>
                                    {item.label}
                                </>
                            </Typography>
                            <i className={`fa-light fa-chevron-right ${styles['open-subcategories']}`} />
                        </>
                    </Typography>
                </Container>
            </button>
            <div className={styles['multilevel-menu-subcategories-list']}>
                <ul className="w-100 p-0">
                    {item.subLinks?.map((subLink, subIndex) => (
                        <SubLink key={subIndex} label={subLink.label} url={subLink.url} closeSidebar={closeSidebar} />
                    ))}
                </ul>
            </div>
        </>
    );
};

interface MenuLinkContentProps {
    item: MenuItem;
    setActiveItem: (value: string | null) => void;
    showSubcategoriesOf: VisibleSubLinks;
    setShowSubcategoriesOf: (value: VisibleSubLinks) => void;
    closeSidebar: () => void;
}

const MenuLinkContent: FC<MenuLinkContentProps> = ({
    item,
    setActiveItem,
    showSubcategoriesOf,
    setShowSubcategoriesOf,
    closeSidebar,
}) => {
    return (
        <ul className="nav d-flex flex-column">
            {item.unCollapses?.map((link, index) => (
                <li
                    key={index}
                    className={
                        `${styles['multilevel-menu-category']}` +
                        ` ${showSubcategoriesOf?.menuItemId !== item.id ? styles['menu-visible'] : ''}` +
                        ` ${showSubcategoriesOf?.subLinksAggregatorId === `id-${item.id}-${link.label}` ? `active ${styles.active}` : ''}` +
                        ` ${showSubcategoriesOf === null || showSubcategoriesOf?.menuItemId === item.id ? 'd-block' : 'd-md-none'}`
                    }>
                    {link.subLinks ? (
                        <SubLinksAggregator
                            menuId={item.id}
                            item={link}
                            setActiveItem={setActiveItem}
                            showSubcategoriesOf={showSubcategoriesOf}
                            setShowSubcategoriesOf={setShowSubcategoriesOf}
                            closeSidebar={closeSidebar}
                        />
                    ) : (
                        <LinkWithoutSubLinks label={link.label} url={link.url || ''} closeSidebar={closeSidebar} />
                    )}
                </li>
            ))}
        </ul>
    );
};
